import { normalizeReturnUrl } from '~/utils/post-sign-in-target';
/**
 * Where a brand-new account goes after /create-account, and when
 * /create-workspace must look up the workspace the account already owns.
 */

/** The opaque proof a recovery SMS link carries (/start?s=). */
const RECOVERY_CLAIM_TOKEN_RE = /^[A-Za-z0-9_-]{32}$/;

/** `/create-workspace?claim=` value marking a recovery sign-up. */
export const RECOVERY_CLAIM_QUERY = 'recovery';

/**
 * `/create-workspace?claim=` value marking any other fresh sign-up (cas-71a4).
 * Sign-up can already have handed the account a workspace (a refused phone-shell
 * claim creates one in the same transaction), so the page looks it up before the
 * plan gate can count it as a "second workspace".
 */
export const POST_SIGNUP_CLAIM_QUERY = 'signup';

export function readRecoveryClaimToken(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === 'string' && RECOVERY_CLAIM_TOKEN_RE.test(raw) ? raw : null;
}

export function resolvePostSignupTarget(input: {
  auditId: string | null;
  recoveryToken: string | null;
  /** cas-bafb: where the person was going (e.g. /pricing?plan=solo). Unsafe values are dropped. */
  returnUrl?: string | null;
}): string {
  if (input.auditId) return `/create-workspace?audit=${encodeURIComponent(input.auditId)}`;
  // cas-4850: sign-up already handed this account its recovered workspace.
  // The bare form would count it and answer with the second-workspace upsell.
  if (input.recoveryToken) return `/create-workspace?claim=${RECOVERY_CLAIM_QUERY}`;
  const returnUrl = normalizeReturnUrl(input.returnUrl ?? null);
  return returnUrl
    ? `/create-workspace?claim=${POST_SIGNUP_CLAIM_QUERY}&returnUrl=${encodeURIComponent(returnUrl)}`
    : `/create-workspace?claim=${POST_SIGNUP_CLAIM_QUERY}`;
}

/**
 * cas-bafb: the page a finished sign-up lands on. A safe returnUrl (the plan page the person
 * left) wins over the default.
 */
export function resolvePostSignupDestination(returnUrl: unknown, fallback: string): string {
  const raw = Array.isArray(returnUrl) ? returnUrl[0] : returnUrl;
  return (typeof raw === 'string' && normalizeReturnUrl(raw)) || fallback;
}

export function shouldResolveOwnedWorkspace(input: {
  isAddBrandFlow: boolean;
  myUid: string | null | undefined;
  pendingAuditId: string | null;
  recoveryClaim: boolean;
  postSignupClaim: boolean;
}): boolean {
  if (input.isAddBrandFlow || !input.myUid) return false;
  return Boolean(input.pendingAuditId) || input.recoveryClaim || input.postSignupClaim;
}
